import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Container } from '@material-ui/core';

import {
  getAllResellers,
  getCampaignTermsAndConditions,
  getSupportedFileFormats,
} from '../../apis';
import { submitCampaignRequest } from '../../apiServices';
import {
  CampaignDetailsFormNew as CampaignDetailsForm,
  CarrierAlertModal,
  CarrierSelectionForm,
  CampaignStatusModal,
} from '../../components';
import {
  CampaignDetailsFormValues,
  CarrierSelectionFormValues,
  FileFormats,
  MnoMetadata,
  Reseller,
} from '../../types';
import { selectDynamicContent } from '@/applications/CspPortal/CSP-Form/selectors';
import { AddResellerModal } from '@/applications/CspPortal/Resellers/components';
import { CampaignBuilderMetadata, campaignController } from '@/apis/core';
import { HelpSection, Loader } from '@/shared_elements';
import { AttachmentFolder, Cnp, UsecaseType } from '@/utils/types';
import { sortMnoNestedObjectToList } from '@/utils';
import { TenDlcCampaignFormValues } from '@/applications/CspPortal/Campaigns/containers/CampaignRegistrationV2/types';

interface TenDlcCampaignRegistrationProps {
  brandUid: string;
  brandName: string;
  useCase: UsecaseType;
  subUseCases: string[];
  metadata?: CampaignBuilderMetadata;
  cnps?: Cnp[];
  value: TenDlcCampaignFormValues;
  onChange: (value: TenDlcCampaignFormValues) => void;
  onPrevious?: () => void;
}

enum ModalType {
  None = 'NONE',
  AddReseller = 'ADD_RESELLER',
  ShowCarrierAlert = 'SHOW_CARRIER_ALERT',
  ConfirmSubmission = 'CONFIRM_SUBMISSION',
}

const TenDlcCampaignRegistration: FunctionComponent<
  TenDlcCampaignRegistrationProps
> = ({
  brandUid,
  brandName,
  useCase,
  subUseCases,
  metadata,
  cnps = [],
  value,
  onChange,
  onPrevious,
}) => {
  const dynamicContent = useSelector(selectDynamicContent);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [
    supportedFileFormatsForSupportingDocument,
    setSupportedFileFormatsForSupportingDocument,
  ] = useState<FileFormats>({});
  const [
    supportedFileFormatsForSampleMultimedia,
    setSupportedFileFormatsForSampleMultimedia,
  ] = useState<FileFormats>({});
  const [termsAndConditions, setTermsAndConditions] = useState<string[]>([]);
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [step, setStep] = useState(2);
  const [mnos, setMnos] = useState<MnoMetadata[]>([]);
  const [greyOutFields, setGreyOutFields] = useState<string[]>([]);
  const [modalType, setModalType] = useState(ModalType.None);
  const [proceeding, setProceeding] = useState(false);
  const { carrierSelectionFormValues, campaignDetailsFormValues } = value;
  const location = useLocation<{ rcsFallback?: Record<string, string> }>();

  const selectedMnos = useMemo(
    () =>
      mnos.filter((mno) =>
        carrierSelectionFormValues.mnoIds.includes(mno.mnoMetadataKey)
      ),
    [mnos, carrierSelectionFormValues.mnoIds]
  );

  const proceed = async () => {
    setProceeding(true);
    const { mnoIds } = carrierSelectionFormValues;
    const {
      supportingDocument,
      sampleMessages,
      sampleMultimedia,
      optinKeywords,
      optoutKeywords,
      helpKeywords,
      ...details
    } = campaignDetailsFormValues;
    const samples: Record<string, string> = {};
    Object.values(sampleMessages).forEach((sampleMessage, index) => {
      samples[`sample${index + 1}`] = sampleMessage.message;
    });
    const formData = {
      subUsecases: subUseCases,
      mnoIds,
      ...details,
      ...samples,
      optinKeywords: optinKeywords.length ? optinKeywords.join(',') : null,
      optoutKeywords: optoutKeywords.length ? optoutKeywords.join(',') : null,
      helpKeywords: helpKeywords.length ? helpKeywords.join(',') : null,
      mmsMediaUuid: sampleMultimedia.map((attachment) => attachment.uuid),
      supportingDocumentUuid: supportingDocument.map(
        (attachment) => attachment.uuid
      ),
    };
    try {
      const response = await submitCampaignRequest(
        brandUid,
        useCase!.id,
        formData
      );
      if (response) {
        const { campaignUid, mnoMetadata } = response;
        const { rcsFallback } = location.state ?? {};
        if (rcsFallback) {
          await campaignController.updateCampaign(rcsFallback.rcsCampaignUid, {
            fallbackCampaignUid: campaignUid,
          });
        }

        history.push({
          pathname: '/campaign/create/successfully',
          state: {
            campaignUid,
            mnoMetadata,
            cspData: dynamicContent,
            ...(rcsFallback
              ? {
                  rcsFallback,
                }
              : {}),
          },
        });
      }
    } finally {
      setProceeding(false);
      setModalType(ModalType.None);
    }
  };

  const handleCarrierSelectionFormValuesChange = (
    carrierSelectionFormValues: CarrierSelectionFormValues
  ) => {
    onChange({
      ...value,
      carrierSelectionFormValues,
    });
  };

  const handleCampaignDetailsFormValuesChange = (
    campaignDetailsFormValues: CampaignDetailsFormValues
  ) => {
    onChange({
      ...value,
      campaignDetailsFormValues,
    });
  };

  useEffect(() => {
    (async () => {
      const [
        supportedFileFormatsResultForSupportingDocument,
        supportedFileFormatsResultForSampleMultimedia,
        termsAndConditionsResult,
        resellersResult,
      ] = await Promise.allSettled([
        getSupportedFileFormats(AttachmentFolder.SupportingDocument),
        getSupportedFileFormats(AttachmentFolder.SampleMultimedia),
        getCampaignTermsAndConditions(),
        getAllResellers(),
      ]);
      if (
        supportedFileFormatsResultForSupportingDocument.status === 'fulfilled'
      ) {
        setSupportedFileFormatsForSupportingDocument(
          supportedFileFormatsResultForSupportingDocument.value ?? {}
        );
      }
      if (
        supportedFileFormatsResultForSampleMultimedia.status === 'fulfilled'
      ) {
        setSupportedFileFormatsForSampleMultimedia(
          supportedFileFormatsResultForSampleMultimedia.value ?? {}
        );
      }
      if (termsAndConditionsResult.status === 'fulfilled') {
        setTermsAndConditions(termsAndConditionsResult.value);
      }
      if (resellersResult.status === 'fulfilled') {
        setResellers(resellersResult.value);
      }
      setLoading(false);
      if (step === 2) {
        setModalType(ModalType.ShowCarrierAlert);
      }
    })();
  }, []);

  useEffect(() => {
    if (metadata) {
      const mnos = sortMnoNestedObjectToList(metadata.mnoMetadata);
      setMnos(mnos);
      setGreyOutFields(metadata.greyoutFields);
      onChange({
        ...value,
        carrierSelectionFormValues: {
          ...carrierSelectionFormValues,
          mnoIds: mnos
            .filter((mno) => mno.qualify)
            .map((mno) => mno.mnoMetadataKey),
        },
      });
    }
  }, [metadata]);

  return (
    <section
      className="campaign-register-section"
      data-testid="campaignRegisterForm"
    >
      <Container
        maxWidth={false}
        className="campaign-register-container selectBrand"
      >
        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="steps-wrapper text-center">
              <ul className="steps-listing list-unstyled">
                <li className={step === 1 ? 'active' : ''}>
                  <div className="step">
                    <span>1</span>
                  </div>
                  <h3 className="secondaryText">Use Case Selection</h3>
                </li>
                <li className={step === 2 ? 'active mno' : 'mno'}>
                  <div className="step">
                    <span>2</span>
                  </div>
                  <h3 className="secondaryText">Carrier Terms Preview</h3>
                </li>
                <li className={step === 3 ? 'active' : ''}>
                  <div className="step">
                    <span>3</span>
                  </div>
                  <h3 className="secondaryText">Campaign Details</h3>
                </li>
              </ul>
            </div>
            {step === 2 && (
              <CarrierSelectionForm
                mnos={mnos}
                defaultValues={carrierSelectionFormValues}
                onPrevious={onPrevious}
                onNext={(values) => {
                  handleCarrierSelectionFormValuesChange(values);
                  setStep(step + 1);
                }}
              />
            )}
            {step === 3 && (
              <CampaignDetailsForm
                greyOutFields={greyOutFields}
                supportedFileFormatsForSupportingDocument={
                  supportedFileFormatsForSupportingDocument
                }
                supportedFileFormatsForSampleMultimedia={
                  supportedFileFormatsForSampleMultimedia
                }
                mnos={selectedMnos}
                termsAndConditions={termsAndConditions}
                cnps={cnps}
                resellers={resellers}
                brandName={brandName}
                useCase={useCase!}
                defaultValues={campaignDetailsFormValues}
                onChange={handleCampaignDetailsFormValuesChange}
                onNewResellerRequest={() => {
                  setModalType(ModalType.AddReseller);
                }}
                onPrevious={() => {
                  setStep(step - 1);
                }}
                onNext={() => {
                  setModalType(ModalType.ConfirmSubmission);
                }}
              />
            )}
          </>
        )}
      </Container>
      <HelpSection
        imgStyle={step === 3 ? { zIndex: 100 } : undefined}
        path={`/campaigns/create#step${step}`}
      />
      <AddResellerModal
        modalType="add"
        open={modalType === ModalType.AddReseller}
        handleModalClick={(open: boolean, reseller?: Reseller) => {
          if (reseller) {
            setResellers([...resellers, reseller]);
            handleCampaignDetailsFormValuesChange({
              ...campaignDetailsFormValues,
              resellerUid: reseller.uid,
            });
          }
          setModalType(open ? ModalType.AddReseller : ModalType.None);
        }}
      />
      <CarrierAlertModal
        open={modalType === ModalType.ShowCarrierAlert}
        onSubmit={() => setModalType(ModalType.None)}
      />
      <CampaignStatusModal
        open={modalType === ModalType.ConfirmSubmission}
        loadFlag={proceeding}
        handleClick={(flag: boolean) => {
          if (flag) {
            proceed();
          } else {
            setModalType(ModalType.None);
          }
        }}
      />
    </section>
  );
};

export default TenDlcCampaignRegistration;
